import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnInit,
  inject,
  HostListener
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  CoinsingReleaseService,
  CoinSingReleaseAuditItem
} from '../../services/coinsing-release.service';
import { formatLastSeenPacific } from '../konobox-status.util';

@Component({
  selector: 'app-konobox-release-history-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './konobox-release-history-modal.component.html',
  styleUrls: ['./konobox-release-history-modal.component.css']
})
export class KonoboxReleaseHistoryModalComponent implements OnInit {
  @Input() deviceIdFilter?: string;
  @Output() close = new EventEmitter<void>();

  private releaseService = inject(CoinsingReleaseService);

  isLoading = true;
  historyList: CoinSingReleaseAuditItem[] = [];
  filteredHistory: CoinSingReleaseAuditItem[] = [];

  // Filters
  filterDevice = '';
  filterVersion = '';
  filterAction = '';
  filterOutcome = ''; // 'all', 'success', 'partial', 'failed'

  // Expanded rows set for bulk releases
  expandedRowIds = new Set<string>();

  // Note snapshot viewer modal
  viewingNoteSnapshot: string | null = null;
  viewingNoteVersion: string = '';

  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKey(event: KeyboardEvent): void {
    if (this.viewingNoteSnapshot !== null) {
      this.closeNoteSnapshot();
      event.stopPropagation();
      return;
    }
    this.closeModal();
  }

  async ngOnInit(): Promise<void> {
    if (this.deviceIdFilter) {
      this.filterDevice = this.deviceIdFilter;
    }
    await this.loadHistory();
  }

  closeModal(): void {
    this.close.emit();
  }

  async loadHistory(): Promise<void> {
    this.isLoading = true;
    try {
      this.historyList = await this.releaseService.getReleaseHistory(undefined, 100);
      this.applyFilters();
    } catch (err) {
      console.error('Failed to load release history:', err);
    } finally {
      this.isLoading = false;
    }
  }

  applyFilters(): void {
    let list = [...this.historyList];

    if (this.filterDevice.trim()) {
      const dev = this.filterDevice.trim().toLowerCase();
      list = list.filter(item => {
        if (item.deviceIds && item.deviceIds.some(id => id.toLowerCase().includes(dev))) {
          return true;
        }
        if (item.results && item.results.some(r => r.deviceId.toLowerCase().includes(dev))) {
          return true;
        }
        return false;
      });
    }

    if (this.filterVersion.trim()) {
      const v = this.filterVersion.trim().toLowerCase();
      list = list.filter(item => (item.version || '').toLowerCase().includes(v));
    }

    if (this.filterAction.trim()) {
      list = list.filter(item => item.action === this.filterAction);
    }

    if (this.filterOutcome) {
      list = list.filter(item => {
        const counts = item.counts;
        if (!counts) return true;
        if (this.filterOutcome === 'success') {
          return counts.failed === 0 && counts.accepted > 0;
        }
        if (this.filterOutcome === 'failed') {
          return counts.accepted === 0 && counts.failed > 0;
        }
        if (this.filterOutcome === 'partial') {
          return counts.accepted > 0 && counts.failed > 0;
        }
        return true;
      });
    }

    this.filteredHistory = list;
  }

  formatWhen(ts: any): string {
    return formatLastSeenPacific(ts);
  }

  toggleRowExpand(id: string): void {
    if (this.expandedRowIds.has(id)) {
      this.expandedRowIds.delete(id);
    } else {
      this.expandedRowIds.add(id);
    }
  }

  isRowExpanded(id: string): boolean {
    return this.expandedRowIds.has(id);
  }

  openNoteSnapshot(item: CoinSingReleaseAuditItem): void {
    this.viewingNoteSnapshot = item.noteSnapshot || 'No note was attached to this release.';
    this.viewingNoteVersion = item.version || '';
  }

  closeNoteSnapshot(): void {
    this.viewingNoteSnapshot = null;
    this.viewingNoteVersion = '';
  }

  getOutcomeSummary(item: CoinSingReleaseAuditItem): string {
    if (!item.counts) return 'Completed';
    const c = item.counts;
    if (c.failed === 0) {
      return `${c.accepted} Succeeded`;
    }
    if (c.accepted === 0) {
      return `${c.failed} Failed`;
    }
    return `${c.accepted} Succeeded, ${c.failed} Failed`;
  }

  isOutcomeSuccess(item: CoinSingReleaseAuditItem): boolean {
    return !!item.counts && item.counts.failed === 0;
  }

  isOutcomeFailed(item: CoinSingReleaseAuditItem): boolean {
    return !!item.counts && item.counts.accepted === 0;
  }

  isOutcomePartial(item: CoinSingReleaseAuditItem): boolean {
    return !!item.counts && item.counts.accepted > 0 && item.counts.failed > 0;
  }
}

